'use client';

import React, { useState } from 'react';
import {
  Box,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Search } from 'lucide-react';

import { ErrorState } from '@/components/common/ErrorState';
import { ReferralHistorySkeleton } from '@/components/skeletons';
import { useAuth, useDebounce, useT } from '@/hooks';
import { useReferralHistory } from '@/services/query/referral.queries';
import type { ReferralUser } from '@/types/referral.types';

/**
 * ReferralHistory Component
 * Displays a paginated, searchable, and sortable table of referred users.
 * Integrates directly with the paginated backend API for high-scale performance.
 */
export const ReferralHistory: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useT();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [sort, setSort] = useState('joinedAt');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');

  const { data, isLoading, error, refetch } = useReferralHistory(
    {
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch,
      sort,
      order,
    },
    isAuthenticated
  );

  const referrals = data?.data || [];
  const total = data?.meta?.total || 0;

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = (property: string) => {
    const isAsc = sort === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setSort(property);
  };

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
    };

  const handleSearchChange = handleInputChange(setSearch);

  const handleSortByName = () => handleSortRequest('name');
  const handleSortByDate = () => handleSortRequest('joinedAt');

  const handleRefetch = () => {
    refetch();
  };

  // Pre-calculated variables for cleaner JSX
  const isNameActive = sort === 'name';
  const isDateActive = sort === 'joinedAt';
  const sortDirection = order.toLowerCase() as 'asc' | 'desc';

  const renderReferralRow = (referral: ReferralUser) => {
    const isActive = referral.status === 'active';

    // Calculate formatted date as a variable
    const date = new Date(referral.joinedAt);
    const formattedDate = isNaN(date.getTime()) ? 'N/A' : format(date, 'MMM d, yyyy');

    return (
      <TableRow
        key={referral.id}
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background-color 0.2s',
        }}
      >
        <TableCell sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'background.default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {referral.name.substring(0, 2).toUpperCase()}
            </Box>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              {referral.name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {formattedDate}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.25,
              py: 0.25,
              borderRadius: 10,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: isActive ? 'success.main' : 'warning.lighter',
              color: isActive ? 'success.contrastText' : 'warning.main',
              border: '1px solid',
              borderColor: isActive ? 'success.main' : 'warning.light',
              textTransform: 'capitalize',
            }}
          >
            {referral.status}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderedReferralRows = referrals.map(renderReferralRow);

  let tableBodyContent;

  if (isLoading) {
    tableBodyContent = <ReferralHistorySkeleton rows={rowsPerPage} />;
  } else if (error) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 2 }}>
          <ErrorState
            compact
            title={t('messages.referral.unableToLoad')}
            message={t('messages.referral.encounteredError')}
            onRetry={handleRefetch}
          />
        </TableCell>
      </TableRow>
    );
  } else if (referrals.length === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {search ? t('messages.referral.noMatchesFound') : t('messages.referral.noReferralsYet')}
          </Typography>
        </TableCell>
      </TableRow>
    );
  } else {
    tableBodyContent = renderedReferralRows;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        {/* Header Actions */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="text.primary">
            {t('messages.referral.recentReferrals')}
          </Typography>

          <TextField
            size="small"
            placeholder={t('messages.referral.searchNameOrWallet')}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: 'action.hover',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>

        {/* Scrollable Table Row Container */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="medium" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ py: 1.5 }}>
                  <TableSortLabel
                    active={isNameActive}
                    direction={isNameActive ? sortDirection : 'asc'}
                    onClick={handleSortByName}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('messages.referral.colUser')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <TableSortLabel
                    active={isDateActive}
                    direction={isDateActive ? sortDirection : 'desc'}
                    onClick={handleSortByDate}
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('messages.referral.colJoinedDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('messages.referral.colStatus')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& > tr:last-child td': { borderBottom: 0 } }}>
              {tableBodyContent}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('referral.rowsPerPage')}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </Box>
    </Box>
  );
};
